import { logger } from '@/services/logger';
import { nanoid } from 'nanoid';
import { createContext, useContext } from 'react';
import { useWebSocket } from './webSocket';

export type messageCallback = (message: MessageEvent) => void;

export enum NotificationTopics {
    project = 'project',
    translation = 'translation',
    scene = 'scene',
    consents = 'consents',
    premiumPlusTrain = 'clip-train',
}

interface Notification {
    subscribe: (topic: NotificationTopics, messageCallback: messageCallback) => string;
    unsubscribe: (topic: NotificationTopics, subscriptionId: string) => void;
}

const Store = createContext<Notification>({} as Notification);
export const useNotifications = () => useContext(Store);

interface Props {
    children: React.ReactNode;
}

export const NotificationProvider = ({ children }: Props) => {
    const subscribers = new Map<NotificationTopics, Map<String, messageCallback>>();

    const subscribe = (topic: NotificationTopics, messageCallback: messageCallback) => {
        const uuid = nanoid();
        const topicSub = subscribers.get(topic) || new Map();
        topicSub.set(uuid, messageCallback);
        subscribers.set(topic, topicSub);
        return uuid;
    };

    const unsubscribe = (topic: NotificationTopics, subscriptionId: string) => {
        const topicSub = subscribers.get(topic);
        if (topicSub) {
            topicSub.delete(subscriptionId);
        }
    };

    const onMessage = (event: MessageEvent) => {
        logger.debug(`WebSocket onMessage fired: ${JSON.stringify(event)}`);

        const message = JSON.parse(event.data);
        const subject = message.messageAttributes?.subject?.stringValue;
        const topic = subject?.split('/')?.[0];
        if (!topic || !Object.values(NotificationTopics).includes(topic as NotificationTopics)) {
            logger.warn(`Unknown topic event`, { event, topic, message, subject });
            return;
        }

        const topicSubscribers = subscribers.get(topic);
        topicSubscribers?.forEach(sub => sub(event));
    };

    useWebSocket(onMessage);

    const notificationRes = {
        subscribe,
        unsubscribe,
    };
    return <Store.Provider value={notificationRes}>{children}</Store.Provider>;
};
